import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

import { StudentDocumentVaultService } from '../../services/student-document-vault/student-document-vault.service';
import {
  StudentDocumentVaultResponse, 
  DocumentCategoryEnum,
  DocumentTypeEnum,
  StudentDocumentVaultSearchResponse
} from '../../models/student-document-vault/student-document-vault';

import { DocumentVaultCardComponent } from './document-vault-card/document-vault-card.component';
import { DocumentUploadModalComponent } from './document-upload-modal/document-upload-modal.component';
import { DocumentVaultSidebarComponent } from './document-vault-sidebar/document-vault-sidebar.component';
import { SidebarModule } from 'primeng/sidebar';

@Component({
  selector: 'app-student-document-vault',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    SkeletonModule,
    AutoCompleteModule,
    SidebarModule,
    FormsModule,
    DocumentVaultCardComponent,
    DocumentUploadModalComponent,
    DocumentVaultSidebarComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './student-document-vault.component.html',
  styleUrls: ['./student-document-vault.component.scss']
})
export class StudentDocumentVaultComponent implements OnInit {
  @ViewChild(DocumentUploadModalComponent) uploadModal!: DocumentUploadModalComponent;
  @ViewChild('desktopSidebar') desktopSidebar!: DocumentVaultSidebarComponent;
  @ViewChild('mobileSidebar') mobileSidebar!: DocumentVaultSidebarComponent;
  mobileSidebarVisible: boolean = false;

  accordionSections = [
    { id: 1, title: 'Identity Documents', category: DocumentCategoryEnum.Identity, isOpen: false, documents: [] as StudentDocumentVaultResponse[], isLoading: false, loaded: false, page: 1, hasMore: true, isFetchingMore: false, filterDocumentId: null as string | null, canScrollLeft: false, canScrollRight: false },
    { id: 2, title: 'Academics', category: DocumentCategoryEnum.Academics, isOpen: false, documents: [] as StudentDocumentVaultResponse[], isLoading: false, loaded: false, page: 1, hasMore: true, isFetchingMore: false, filterDocumentId: null as string | null, canScrollLeft: false, canScrollRight: false },
    { id: 3, title: 'Essays & Statements of Purpose', category: DocumentCategoryEnum.EssayPilot, isOpen: false, documents: [] as StudentDocumentVaultResponse[], isLoading: false, loaded: false, page: 1, hasMore: true, isFetchingMore: false, filterDocumentId: null as string | null, canScrollLeft: false, canScrollRight: false },
    { id: 4, title: 'Letter of Recommendation', category: DocumentCategoryEnum.LOR, isOpen: false, documents: [] as StudentDocumentVaultResponse[], isLoading: false, loaded: false, page: 1, hasMore: true, isFetchingMore: false, filterDocumentId: null as string | null, canScrollLeft: false, canScrollRight: false },
    { id: 5, title: 'Test Scores', category: DocumentCategoryEnum.TestScores, isOpen: false, documents: [] as StudentDocumentVaultResponse[], isLoading: false, loaded: false, page: 1, hasMore: true, isFetchingMore: false, filterDocumentId: null as string | null, canScrollLeft: false, canScrollRight: false },
    { id: 6, title: 'Other Documents', category: DocumentCategoryEnum.Other, isOpen: false, documents: [] as StudentDocumentVaultResponse[], isLoading: false, loaded: false, page: 1, hasMore: true, isFetchingMore: false, filterDocumentId: null as string | null, canScrollLeft: false, canScrollRight: false }
  ];

  activeCategoryId: number | null = null;
  activeStatusFilter: number = 1; // 1 = All, 2 = Uploaded, 3 = Missing
  statusCounts: any = { all: 0, uploaded: 0, missing: 0 };
  
  skeletonArray = [1, 2, 3, 4];
  isModalVisible: boolean = false;
  DocumentCategoryEnum = DocumentCategoryEnum;
  DocumentTypeEnum = DocumentTypeEnum;

  // Search properties
  searchTitle: string = '';
  searchResults: StudentDocumentVaultSearchResponse[] = [];
  searchSubject = new Subject<string>();

  constructor(
    private docsService: StudentDocumentVaultService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadStatusCounts();
    // loadStatusCounts will trigger loadDocuments of the active section
  }

  loadStatusCounts(): void {
    this.docsService.getStatusCounts().subscribe({
      next: (res: any) => {
        this.statusCounts = res?.responseData ?? res?.result ?? { all: 0, uploaded: 0, missing: 0 };
        
        // After getting status counts, load the active section documents
        this.accordionSections.forEach(sec => {
            if (sec.isOpen) this.loadSectionDocuments(sec);
        });
        this.refreshSidebarCounts();
      },
      error: () => {
        // Fallback if API fails
        this.accordionSections.forEach(sec => {
            if (sec.isOpen) this.loadSectionDocuments(sec);
        });
      }
    });
  }

  setStatusFilter(filter: number): void {
    this.activeStatusFilter = filter;
    // Reload open sections
    this.accordionSections.forEach(sec => {
      if (sec.isOpen) this.loadSectionDocuments(sec);
    });
    this.refreshSidebarCounts();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query.trim()) {
          this.searchResults = [];
          return of(null);
        }
        return this.docsService.searchDocumentsByTitle(query).pipe(
          catchError(() => of({ result: [] }))
        );
      })
    ).subscribe((res: any) => {
      if (res) {
        this.searchResults = res?.responseData ?? res?.result ?? res ?? [];
      }
    });
  }

  onSearch(event: any): void {
    this.searchSubject.next(event.query);
  }

  onSelectSearchResult(event: any): void {
    const selectedDoc = event.value as StudentDocumentVaultSearchResponse;
    if (!selectedDoc) return;

    this.activeStatusFilter = 1; // Reset to All Documents to find the specific document
    
    // Find section
    const section = this.accordionSections.find(s => s.category === selectedDoc.category);
    if (section) {
      // Set filter and reload
      section.filterDocumentId = selectedDoc.id;
      
      if (!section.isOpen) {
        this.toggleSection(section);
      } else {
        this.loadSectionDocuments(section);
      }
      
      // Wait for section to be potentially loaded
      setTimeout(() => {
        this.scrollToDocument(selectedDoc.id);
      }, 800);
    }
    
    // Clear search input
    this.searchTitle = '';
    this.searchResults = [];
  }

  clearFilter(section: any): void {
    section.filterDocumentId = null;
    this.loadSectionDocuments(section);
  }

  private scrollToDocument(docId: string): void {
    const element = document.getElementById(`doc-card-${docId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      
      // Add a brief highlight effect
      element.classList.add('highlight-card');
      setTimeout(() => element.classList.remove('highlight-card'), 2000);
    }
  }

  toggleSection(section: any): void {
    if (section.isOpen) {
      section.isOpen = false;
      this.activeCategoryId = null;
      return;
    }

    this.accordionSections.forEach(sec => sec.isOpen = false);
    section.isOpen = true;
    this.activeCategoryId = section.category;

    if (section.isOpen) {
      this.loadSectionDocuments(section);
    }
  }

  handleCategorySelect(category: DocumentCategoryEnum): void {
    this.activeCategoryId = category;
    this.activeStatusFilter = 1; // Reset to All Documents as per requirement
    const section = this.accordionSections.find(s => s.category === category);
    if (section) {
      this.accordionSections.forEach(s => s.isOpen = false);
      section.isOpen = true;
      this.loadSectionDocuments(section);
      
      // Optional: Scroll to the section element if needed
      setTimeout(() => {
        const element = document.getElementById('section-' + section.id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }

  loadSectionDocuments(section: any): void {
    section.isLoading = true;
    section.page = 1;
    section.hasMore = true;
    this.docsService.getDocumentVaults(section.page, 10, section.category, section.filterDocumentId || undefined, this.activeStatusFilter).subscribe({
      next: (res: any) => {
        const data = res?.responseData ?? res?.result ?? [];
        const total = res?.totalCount ?? res?.total ?? 0;
        section.documents = data;
        
        if (section.filterDocumentId || section.documents.length >= total || data.length < 10) {
          section.hasMore = false;
        }
        
        section.isLoading = false;
        section.loaded = true;

        // Check scroll status after DOM update
        setTimeout(() => {
          const container = document.getElementById('scroll-container-' + section.id);
          if (container) this.updateScrollStatus(container, section);
        }, 300);
      },
      error: () => {
        section.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load ' + section.title });
      }
    });
  }

  loadMoreDocuments(section: any): void {
    if (!section.hasMore || section.isFetchingMore || section.isLoading) return;
    
    section.isFetchingMore = true;
    section.page += 1;
    
    this.docsService.getDocumentVaults(section.page, 10, section.category, undefined, this.activeStatusFilter).subscribe({
      next: (res: any) => {
        const data = res?.responseData ?? res?.result ?? [];
        const total = res?.totalCount ?? res?.total ?? 0;
        
        if (data.length > 0) {
          section.documents = [...section.documents, ...data];
        }
        
        if (section.documents.length >= total || data.length < 10) {
          section.hasMore = false;
        }
        section.isFetchingMore = false;

        // Check scroll status after more data is added
        setTimeout(() => {
          const container = document.getElementById('scroll-container-' + section.id);
          if (container) this.updateScrollStatus(container, section);
        }, 300);
      },
      error: () => {
        section.isFetchingMore = false;
        section.page -= 1; // Revert page on failure
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load more ' + section.title });
      }
    });
  }

  scrollSection(container: HTMLElement, direction: 'left' | 'right'): void {
    const scrollAmount = 300; 
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }

  onScroll(event: Event, section: any): void {
    const element = event.target as HTMLElement;
    this.updateScrollStatus(element, section);
    
    // Check if scrolled near the right edge (within 50px)
    if (element.scrollWidth - element.scrollLeft - element.clientWidth <= 50) {
      this.loadMoreDocuments(section);
    }
  }

  updateScrollStatus(element: HTMLElement, section: any): void {
    section.canScrollLeft = element.scrollLeft > 5;
    section.canScrollRight = element.scrollWidth > (element.scrollLeft + element.clientWidth + 5);
  }

  loadDocuments(): void {
    this.loadStatusCounts();
  }

  refreshSidebarCounts(): void {
    this.desktopSidebar?.loadCategoryCounts();
    this.mobileSidebar?.loadCategoryCounts();
  }

  showUploadDialog(category?: DocumentCategoryEnum, docType?: DocumentTypeEnum): void {
    if (this.uploadModal) {
      this.uploadModal.showForCreate(category, docType);
    }
  }

  editDocument(doc: StudentDocumentVaultResponse): void {
    if (this.uploadModal) {
      this.uploadModal.showForEdit(doc);
    }
  }

  deleteDocument(id: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this document permantly? This action cannot be undone.',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      accept: () => {
        this.docsService.deleteDocumentVault(id).subscribe({
          next: (res: any) => {
            if (res === true) {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document deleted successfully' });
              this.loadDocuments();
            } else {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete document' });
            }
          },
          error: (err: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete document' });
          }
        });
      }
    });
  }

  getShortCategoryName(category: DocumentCategoryEnum): string {
    switch (category) {
      case DocumentCategoryEnum.EssayPilot: return 'Essay';
      case DocumentCategoryEnum.LOR: return 'LOR';
      case DocumentCategoryEnum.Identity: return 'Identity';
      case DocumentCategoryEnum.Academics: return 'Academic';
      case DocumentCategoryEnum.TestScores: return 'Test Score';
      case DocumentCategoryEnum.Other: return 'Document';
      default: return 'Document';
    }
  }

  getCategoryLabel(category: DocumentCategoryEnum): string {
    switch (category) {
      case DocumentCategoryEnum.EssayPilot: return 'Essays';
      case DocumentCategoryEnum.LOR: return 'LOR';
      case DocumentCategoryEnum.Identity: return 'Identity';
      case DocumentCategoryEnum.Academics: return 'Academics';
      case DocumentCategoryEnum.TestScores: return 'Test Scores';
      case DocumentCategoryEnum.Other: return 'Other';
      default: return 'Other';
    }
  }
}
